if (!customElements.get('cart-crosselling')) {
  customElements.define(
    'cart-crosselling',
    class CartCrosselling extends HTMLElement {
      constructor() {
        super();
        this.fetchTimeout = null;
      }

      connectedCallback() {
        window._cartCrossellingCache = window._cartCrossellingCache || {};

        this.driverId = this.computeDriverId();
        if (!this.driverId) {
          this.hide();
          return;
        }

        if (window._cartCrossellingCache[this.driverId]) {
          this.applyCached(window._cartCrossellingCache[this.driverId]);
        } else {
          this.scheduleFetch();
        }

        this.bindCardEvents();
        this.bindOutsideClickToClosePickers();
      }

      disconnectedCallback() {
        if (this.fetchTimeout) clearTimeout(this.fetchTimeout);
      }

      computeDriverId() {
        const script = this.querySelector('script[data-cart-items]');
        if (!script) return null;
        let items = [];
        try {
          items = JSON.parse(script.textContent);
        } catch (e) {
          return null;
        }
        if (!items.length) return null;
        let driver = items[0];
        for (let i = 1; i < items.length; i++) {
          if (items[i].final_line_price > driver.final_line_price) {
            driver = items[i];
          }
        }
        return driver.product_id;
      }

      scheduleFetch() {
        if (this.fetchTimeout) clearTimeout(this.fetchTimeout);
        this.fetchTimeout = setTimeout(() => this.loadRecommendations(), 150);
      }

      loadRecommendations() {
        if (!this.driverId) return;
        if (window._cartCrossellingCache[this.driverId]) {
          this.applyCached(window._cartCrossellingCache[this.driverId]);
          return;
        }

        const url = `${this.dataset.url}?section_id=${this.dataset.sectionId}&product_id=${this.driverId}&intent=complementary&limit=${this.dataset.limit}`;

        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            const parsed = new DOMParser().parseFromString(text, 'text/html');
            const incoming = parsed.querySelector('cart-crosselling');
            if (!incoming) return;

            const innerHTML = incoming.innerHTML.trim();
            window._cartCrossellingCache[this.driverId] = innerHTML;
            this.applyCached(innerHTML);
          })
          .catch((e) => {
            console.error('[cart-crosselling] fetch failed', e);
          });
      }

      applyCached(innerHTML) {
        this.innerHTML = innerHTML;
        this.filterCartProducts();
        this.toggleHiddenState();
        this.bindCardEvents();
        this.bindNavButtons();
      }

      filterCartProducts() {
        const cartIds = (this.dataset.cartProductIds || '')
          .split(',')
          .filter(Boolean);
        if (!cartIds.length) return;
        this.querySelectorAll('.cart-crosselling__item').forEach((item) => {
          const card = item.querySelector('[data-product-id]');
          if (card && cartIds.includes(card.dataset.productId)) {
            item.remove();
          }
        });
      }

      toggleHiddenState() {
        const hasItems = !!this.querySelector('.cart-crosselling__item');
        this.classList.toggle('cart-crosselling--hidden', !hasItems);
      }

      bindNavButtons() {
        const scroller = this.querySelector('[data-scroller]');
        const prev = this.querySelector('[data-nav="prev"]');
        const next = this.querySelector('[data-nav="next"]');
        if (!scroller || !prev || !next) return;

        const step = () => {
          const firstItem = scroller.querySelector('.cart-crosselling__item');
          if (!firstItem) return scroller.clientWidth;
          const gap = parseFloat(getComputedStyle(scroller).columnGap || getComputedStyle(scroller).gap) || 0;
          return firstItem.getBoundingClientRect().width + gap;
        };

        const updateNavState = () => {
          const max = scroller.scrollWidth - scroller.clientWidth;
          prev.disabled = scroller.scrollLeft <= 1;
          next.disabled = scroller.scrollLeft >= max - 1;
        };

        prev.addEventListener('click', () => {
          scroller.scrollBy({ left: -step(), behavior: 'smooth' });
        });
        next.addEventListener('click', () => {
          scroller.scrollBy({ left: step(), behavior: 'smooth' });
        });
        scroller.addEventListener('scroll', updateNavState, { passive: true });

        updateNavState();
      }

      hide() {
        this.classList.add('cart-crosselling--hidden');
      }

      bindCardEvents() {
        this.querySelectorAll('.cart-crosselling-card').forEach((card) => {
          if (card.dataset.bound === 'true') return;
          card.dataset.bound = 'true';

          const addBtn = card.querySelector('[data-action="add"]');
          if (addBtn) {
            addBtn.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();
              this.onAddClick(card, addBtn);
            });
          }

          const confirmBtn = card.querySelector('[data-action="confirm"]');
          if (confirmBtn) {
            confirmBtn.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();
              this.onConfirmClick(card, confirmBtn);
            });
          }
        });
      }

      onAddClick(card, button) {
        const hasVariants = card.dataset.hasVariants === 'true';
        if (hasVariants) {
          this.togglePicker(card, true);
          return;
        }
        const variantId = button.dataset.variantId;
        if (!variantId) return;
        this.addToCart(variantId, button);
      }

      onConfirmClick(card, button) {
        const picker = card.querySelector('[data-picker]');
        if (!picker) return;
        const variantsScript = picker.querySelector('script[data-variants]');
        if (!variantsScript) return;

        let variants = [];
        try {
          variants = JSON.parse(variantsScript.textContent);
        } catch (e) {
          return;
        }

        const selects = picker.querySelectorAll('select[data-option-index]');
        const selected = Array.from(selects).map((s) => s.value);

        const match = variants.find((v) => {
          if (!Array.isArray(v.options)) return false;
          if (v.options.length !== selected.length) return false;
          return v.options.every((opt, i) => opt === selected[i]);
        });

        const errorEl = picker.querySelector('[data-picker-error]');
        if (!match) {
          this.showPickerError(errorEl, 'Please choose valid options.');
          return;
        }
        if (!match.available) {
          this.showPickerError(errorEl, 'This variant is sold out.');
          return;
        }
        this.hidePickerError(errorEl);
        this.addToCart(match.id, button, card);
      }

      showPickerError(el, message) {
        if (!el) return;
        el.textContent = message;
        el.hidden = false;
      }

      hidePickerError(el) {
        if (!el) return;
        el.hidden = true;
        el.textContent = '';
      }

      togglePicker(card, open) {
        const picker = card.querySelector('[data-picker]');
        if (!picker) return;
        const addBtn = card.querySelector('[data-action="add"]');

        if (open) {
          this.querySelectorAll('.cart-crosselling-card [data-picker]').forEach((p) => {
            if (p !== picker) p.hidden = true;
          });
          picker.hidden = false;
          addBtn?.setAttribute('aria-expanded', 'true');
        } else {
          picker.hidden = true;
          addBtn?.setAttribute('aria-expanded', 'false');
        }
      }

      bindOutsideClickToClosePickers() {
        if (window._cartCrossellingOutsideBound) return;
        window._cartCrossellingOutsideBound = true;
        document.addEventListener('click', (event) => {
          const activeElement = document.querySelector('cart-crosselling');
          if (!activeElement) return;
          const openPickers = activeElement.querySelectorAll('.cart-crosselling-card [data-picker]:not([hidden])');
          openPickers.forEach((picker) => {
            const card = picker.closest('.cart-crosselling-card');
            if (card && !card.contains(event.target)) {
              picker.hidden = true;
              const addBtn = card.querySelector('[data-action="add"]');
              addBtn?.setAttribute('aria-expanded', 'false');
            }
          });
        });
      }

      addToCart(variantId, button, card) {
        const cart = document.querySelector('cart-drawer');
        if (!cart) return;

        button.setAttribute('aria-busy', 'true');

        const sections = cart.getSectionsToRender().map((s) => s.id);

        const body = JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: {
            _added_via: 'cart_crosselling',
          },
          sections: sections.join(','),
          sections_url: window.location.pathname,
        });

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = body;

        cart.setActiveElement(document.activeElement);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'cart-crosselling',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-crosselling',
              productVariantId: variantId,
              cartData: response,
            });

            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');

            cart.renderContents(response);

            if (card) this.togglePicker(card, false);
          })
          .catch((e) => {
            console.error('[cart-crosselling] add to cart failed', e);
          })
          .finally(() => {
            button.removeAttribute('aria-busy');
          });
      }
    }
  );
}
